package org.example.project.engineletter

import org.example.project.dashboard.AppDialogs

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import org.example.project.enginekartei.KarteiApi
import org.example.project.enginetabular.currentDateDE
import org.example.project.registrierung.RegistrierungApi
import org.example.project.service.CustomerApi
import org.example.project.service.LoginService
import org.example.project.service.UserSettingsApi
import org.example.project.service.YardApi
import org.example.project.service.ZustandManager
import org.example.project.settings.LogoSettings
import org.example.project.utils.All
import org.example.project.datenschutz.SignaturDatenschutzOverlay
import org.example.project.engineletter.layouts.VollmachtSysLayout
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLInputElement
import org.w3c.files.FileReader
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.InputType

private const val LETTER_LAYOUT_BRIEF = "BRIEF"
private const val LETTER_LAYOUT_BRIEF_SYS = "BRIEF_SYS"
private const val LETTER_LAYOUT_VOLLMACHT_SYS = "VOLLMACHT_SYS"
const val LETTER_LAYOUT_RECHNUNG_SYS = "RECHNUNG_SYS"
private const val LETTER_EMPFAENGER_FREITEXT = "__FREITEXT__"
private const val LETTER_SCREEN_KEY = "engineletter"
private const val LETTER_PREVIEW_ROOT_ID = "letter-preview-root"
private const val LETTER_FOOTER_COUNT = 3
private const val LETTER_HEADER_LOGO = "LOGO"
private const val LETTER_FOOTER_BUSINESS = "BUSINESS"
private const val LETTER_TEXT_TO_PDF_LOGO_FILE = "text-to-pdf-logo.txt"
private val LETTER_TEXT_TO_PDF_DEFAULT_LOGO = LogoSettings.letterPdfDefaultLogo
private const val LETTER_VOLLMACHT_SIGNATURE_CANVAS_ID = "letter-vollmacht-signatur-canvas"

private fun resolvedTextToPdfLogo(value: String): String {
    val source = value.trim()
    return if (
        source.startsWith("data:image/") ||
        source.startsWith("images/") ||
        source.startsWith("/images/") ||
        source.startsWith("https://") ||
        source.startsWith("http://")
    ) source else LETTER_TEXT_TO_PDF_DEFAULT_LOGO
}

private val textToPdfInvoiceSample = All(
    id = "RE-2026-001",
    datum = "2026_08",
    rechnungsdatum = "10.08.2026",
    onboardingPreis = "250,00",
    onboardingPreisEinheit = "250,00",
    lizensierungPreis = "500,00",
    lizensierungPreisEinheit = "500,00",
    crKosten = "100,00",
    crStd = "2",
    crPreisProStd = "50,00",
    meetingKosten = "100,00",
    meetingStd = "2",
    meetingPreisProStd = "50,00",
    schulungKosten = "100,00",
    schulungStd = "2",
    schulungPreisProStd = "50,00",
    beratungKosten = "50,00",
    beratungStd = "1",
    beratungPreisProStd = "50,00",
    gesamtbetragEuro = "1.100,00"
)

private val textToPdfQmDefinitionSamples = listOf(
    All(id = "1", kontrollId = "1", bezeichnung = "temperaturkontrolle", kontrollBezeichnung = "Welche Temperatur wurde im Laderaum gemessen?", aktivWe = true, aktivWa = true, kontrollTyp = "number"),
    All(id = "2", kontrollId = "2", bezeichnung = "schädlingskontrolle", kontrollBezeichnung = "Keine Spuren von Schädlingen im Laderaum", aktivWe = true, aktivWa = true, kontrollTyp = "checkbox", istCheckbox = true),
    All(id = "3", kontrollId = "3", bezeichnung = "sauberkeit", kontrollBezeichnung = "Visuelle Prüfung auf Verschmutzung / Verunreinigung", aktivWe = true, aktivWa = true, kontrollTyp = "checkbox", istCheckbox = true),
    All(id = "5", kontrollId = "5", bezeichnung = "verpackungBeschädigt", kontrollBezeichnung = "Äußere Verpackung ohne Schäden", aktivWe = true, aktivWa = true, kontrollTyp = "checkbox", istCheckbox = true)
)

private val textToPdfProcessWeSample = All(
    id = "WE-2026-001",
    auftragsart = "WE",
    lieferscheinnummer = "LS-WE-10001",
    empfaenger = "Musterempfänger\nEmpfängerweg 1\n41836 Hückelhoven",
    lieferant = "Musterlieferant\nLieferantweg 2\n41836 Hückelhoven",
    auftraggeber = "Musterauftraggeber\nAuftraggeberweg 3\n41836 Hückelhoven",
    unternehmer = "Musterunternehmer\nUnternehmerweg 4\n41836 Hückelhoven",
    fahrername = "Max Mustermann",
    lkwkennzeichen = "MG-LS 2026",
    aufliegerkennzeichen = "MG-A 1001",
    typ = "EUR",
    slotLhmTyp = "EUR",
    palettenanzahl = 66,
    leerpalettenanzahl = 66,
    yardabschlussIstPaletten = 66,
    palettenWerte = mapOf(
        "mobil.kontrolle.1" to "7",
        "mobil.kontrolle.2" to "true",
        "mobil.kontrolle.3" to "true",
        "mobil.kontrolle.5" to "true",
        "mobil.wePalettenUnbeschädigt" to "66",
        "mobil.wePalettenFehlmenge" to "0",
        "mobil.wePalettenBruch" to "0",
        "mobil.wePalettenÜberzählig" to "0"
    ),
    waSammelPin = "4711",
    tor = 3,
    name = "T. Stolz"
)

private val textToPdfProcessWaSample = textToPdfProcessWeSample.copy(
    id = "WA-2026-001",
    auftragsart = "WA",
    typ = "EUR",
    slotLhmTyp = "EUR",
    lieferscheinnummer = "LS-WA-20001",
    empfaenger = "Musterempfänger\nEmpfängerweg 1\n41836 Hückelhoven",
    lieferant = "Musterlieferant\nLieferantweg 2\n41836 Hückelhoven",
    auftraggeber = "Musterauftraggeber\nAuftraggeberweg 3\n41836 Hückelhoven",
    unternehmer = "Musterunternehmer\nUnternehmerweg 4\n41836 Hückelhoven",
    tor = 5,
    waSammelPin = "8150"
)

private val textToPdfPalletAccountSample = textToPdfProcessWaSample.copy(
    abschlussMonatLabel = "August 2026",
    tauschpartner = "Musterempfänger",
    tauschpartnerAdresse = "Empfängerweg 1",
    tauschpartnerOrt = "41836 Hückelhoven",
    eingaengeGesamt = "66",
    ausgaengeGesamt = "66",
    bewegungenGesamt = "132",
    kontostand = "0",
    palettenart = "EUR"
)

private val textToPdfErrorProtocolSample = All(
    kontrollPositionen = (1..30).map { position ->
        All(
            typ = when (position) {
                in 1..10 -> "Bruch"
                in 11..20 -> "Fehlmenge"
                else -> "Überzählig"
            },
            nve = "Charge ${position.toString().padStart(5, '0')}",
            lcode = position.toString().padStart(5, '0'),
            mhd = "01.01.2030",
            artikelNummer = (1111 + position).toString().padStart(5, '0'),
            menge = "5"
        )
    }
)

private val textToPdfProcessWeLogSample = listOf(
    All(typ = "SLOT_GEBUCHT", notiz = "Auftrag gebucht", feedUhrzeit = "07:50", timestamp = "0"),
    All(typ = "LIEFERUNG_ANGEMELDET", notiz = "Wareneingang angemeldet", feedUhrzeit = "08:05", timestamp = "900000"),
    All(typ = "LKW_EINGETROFFEN", notiz = "LKW eingetroffen", feedUhrzeit = "08:20", timestamp = "1800000"),
    All(typ = "TOR_ZUGEWIESEN", notiz = "Tor zugewiesen", torZugewiesenUhrzeit = "08:25", timestamp = "2100000"),
    All(typ = "PIN_ABFRAGE", notiz = "PIN-Abfrage", feedUhrzeit = "08:28", timestamp = "2280000"),
    All(typ = "WE_TEILABSCHLUSS", notiz = "Teilentladen", feedUhrzeit = "08:45", timestamp = "3300000"),
    All(typ = "WE_ABGESCHLOSSEN", notiz = "Vorgang abgeschlossen", feedUhrzeit = "09:00", timestamp = "4200000"),
    All(typ = "TOR_FREI", notiz = "Tor frei", feedUhrzeit = "09:05", timestamp = "4500000")
)

private val textToPdfProcessWaLogSample = listOf(
    All(typ = "SLOT_GEBUCHT", notiz = "Auftrag gebucht", feedUhrzeit = "09:50", timestamp = "0"),
    All(typ = "LKW_EINGETROFFEN", notiz = "LKW eingetroffen", feedUhrzeit = "10:20", timestamp = "1800000"),
    All(typ = "TOR_ZUGEWIESEN", notiz = "Tor zugewiesen", torZugewiesenUhrzeit = "10:25", timestamp = "2100000"),
    All(typ = "PIN_ABFRAGE", notiz = "PIN-Abfrage", feedUhrzeit = "10:28", timestamp = "2280000"),
    All(typ = "WA_LKW_TEILABSCHLUSS", notiz = "Teilverladen", feedUhrzeit = "10:45", timestamp = "3300000"),
    All(typ = "WA_LKW_ABGESCHLOSSEN", notiz = "Vorgang abgeschlossen", feedUhrzeit = "11:00", timestamp = "4200000"),
    All(typ = "TOR_FREI", notiz = "Tor frei", feedUhrzeit = "11:05", timestamp = "4500000")
)

private val letterProfileJson = Json {
    ignoreUnknownKeys = true
    prettyPrint = false
}

typealias LetterDocumentState = All

fun LetterDocumentState(
    layoutKey: String = LETTER_LAYOUT_BRIEF_SYS,
    selectedUserId: String = "",
    selectedEmpfaengerKey: String = LETTER_EMPFAENGER_FREITEXT,
    dokumentName: String = "brief.pdf",
    firmenName: String = "",
    firmenAdresse: String = "",
    kopfzeilen: String = "",
    empfaenger: String = "",
    betreff: String = "Ihr Schreiben",
    text: String = "",
    headerMode: String = LETTER_HEADER_LOGO,
    footerMode: String = LETTER_FOOTER_BUSINESS,
    grussformel: String = "Mit freundlichen Grüßen",
    signaturOrtDatum: String = "",
    signaturName: String = "",
    logoSrc: String = LETTER_TEXT_TO_PDF_DEFAULT_LOGO,
    footerLines: List<String> = List(LETTER_FOOTER_COUNT) { "" }
): All = All(
    typ = layoutKey,
    id = selectedUserId,
    key = selectedEmpfaengerKey,
    name = dokumentName,
    firma = firmenName,
    anschrift = firmenAdresse,
    bemerkung = kopfzeilen,
    empfaenger = empfaenger,
    briefBetreff = betreff,
    briefText = text,
    headerMode = headerMode,
    footerMode = footerMode,
    briefGrussformel = grussformel,
    signaturOrtDatum = signaturOrtDatum,
    signatur = signaturName,
    logoSrc = logoSrc,
    module = footerLines.normalizedFooterLines().map { All(label = it) }
)

val LetterDocumentState.layoutKey: String get() = typ.ifBlank { LETTER_LAYOUT_BRIEF_SYS }
val LetterDocumentState.selectedUserId: String get() = id
val LetterDocumentState.selectedEmpfaengerKey: String get() = key.ifBlank { LETTER_EMPFAENGER_FREITEXT }
val LetterDocumentState.firmenName: String get() = firma
val LetterDocumentState.firmenAdresse: String get() = anschrift
val LetterDocumentState.kopfzeilen: String get() = bemerkung
val LetterDocumentState.betreff: String get() = briefBetreff.ifBlank { "Ihr Schreiben" }
val LetterDocumentState.text: String get() = briefText
val LetterDocumentState.grussformel: String get() = briefGrussformel.ifBlank { "Mit freundlichen Grüßen" }
val LetterDocumentState.signaturName: String get() = signatur
val LetterDocumentState.footerLines: List<String> get() = module.map { it.label }

private fun LetterDocumentState.resolvedDokumentName(): String = dokumentName.ifBlank { name.ifBlank { "brief.pdf" } }
private fun LetterDocumentState.resolvedHeaderMode(): String = LETTER_HEADER_LOGO
private fun LetterDocumentState.resolvedFooterMode(): String = LETTER_FOOTER_BUSINESS

fun LetterDocumentState.copy(
    layoutKey: String = this.layoutKey,
    selectedUserId: String = this.selectedUserId,
    selectedEmpfaengerKey: String = this.selectedEmpfaengerKey,
    dokumentName: String = this.resolvedDokumentName(),
    firmenName: String = this.firmenName,
    firmenAdresse: String = this.firmenAdresse,
    kopfzeilen: String = this.kopfzeilen,
    empfaenger: String = this.empfaenger,
    betreff: String = this.betreff,
    text: String = this.text,
    headerMode: String = this.resolvedHeaderMode(),
    footerMode: String = this.resolvedFooterMode(),
    grussformel: String = this.grussformel,
    signaturOrtDatum: String = this.signaturOrtDatum,
    signaturName: String = this.signaturName,
    logoSrc: String = this.logoSrc,
    footerLines: List<String> = this.footerLines
): LetterDocumentState = LetterDocumentState(
    layoutKey = layoutKey,
    selectedUserId = selectedUserId,
    selectedEmpfaengerKey = selectedEmpfaengerKey,
    dokumentName = dokumentName,
    firmenName = firmenName,
    firmenAdresse = firmenAdresse,
    kopfzeilen = kopfzeilen,
    empfaenger = empfaenger,
    betreff = betreff,
    text = text,
    headerMode = headerMode,
    footerMode = footerMode,
    grussformel = grussformel,
    signaturOrtDatum = signaturOrtDatum,
    signaturName = signaturName,
    logoSrc = logoSrc,
    footerLines = footerLines
)

@Composable
fun LetterEngine() {
    val scope = rememberCoroutineScope()

    var kunden by remember { mutableStateOf<List<All>>(emptyList()) }
    var unternehmen by remember { mutableStateOf(All()) }
    var isSaving by remember { mutableStateOf(false) }
    var showLayoutGallery by remember { mutableStateOf(true) }
    var defaultLogoSrc by remember { mutableStateOf(LETTER_TEXT_TO_PDF_DEFAULT_LOGO) }
    var weAuftrag by remember { mutableStateOf<All?>(null) }
    var waAuftrag by remember { mutableStateOf<All?>(null) }
    var weVorgangslog by remember { mutableStateOf<List<All>>(emptyList()) }
    var waVorgangslog by remember { mutableStateOf<List<All>>(emptyList()) }
    var kontrollDefinitionen by remember { mutableStateOf<List<All>>(emptyList()) }
    var bevollmaechtigteName by remember { mutableStateOf("") }
    var bevollmaechtigteSignatur by remember { mutableStateOf("") }
    var vollmachtSignaturOffen by remember { mutableStateOf(false) }
    var vollmachtCanvasGezeichnet by remember { mutableStateOf(false) }
    var state by remember {
        mutableStateOf(
            LetterDocumentState(
                selectedUserId = LoginService.getId(),
                signaturName = LoginService.getUser()?.let { "${it.vorname} ${it.nachname}" }.orEmpty()
            )
        )
    }

    fun userSignaturName(): String =
        LoginService.getUser()?.let { "${it.vorname} ${it.nachname}" }.orEmpty()

    fun normalizeDocument(doc: LetterDocumentState): LetterDocumentState =
        doc.copy(
            layoutKey = doc.layoutKey.ifBlank { LETTER_LAYOUT_BRIEF_SYS },
            selectedUserId = LoginService.getId(),
            signaturName = doc.signaturName.ifBlank { userSignaturName() },
            headerMode = LETTER_HEADER_LOGO,
            footerMode = LETTER_FOOTER_BUSINESS,
            footerLines = doc.footerLines.normalizedFooterLines()
        )

    fun applyFirmendaten(base: LetterDocumentState): LetterDocumentState {
        val ort = unternehmen.ort
        val datum = currentDateDE()
        val firmenAdresse = buildAddressBlock(
            strasse = unternehmen.strasse,
            hausnummer = unternehmen.hausnummer,
            plz = unternehmen.plz,
            ort = unternehmen.ort
        )
        return base.copy(
            firmenName = unternehmen.firma,
            firmenAdresse = firmenAdresse,
            kopfzeilen = if (ort.isBlank()) datum else "$ort, $datum",
            signaturOrtDatum = if (ort.isBlank()) datum else "$ort, $datum",
            footerLines = base.footerLines.normalizedFooterLines().withFallback(
                buildUnternehmenFooterLines(
                    firmenName = unternehmen.firma,
                    firmenAdresse = firmenAdresse,
                    webseite = unternehmen.webseite,
                    email = unternehmen.email,
                    telefon = unternehmen.telefon,
                    fax = unternehmen.fax,
                    kreditinstitut = unternehmen.kreditinstitut,
                    iban = unternehmen.iban,
                    umsatzsteuerId = unternehmen.umsatzsteuerId
                )
            )
        )
    }

    fun applyBriefSysDefaults(base: LetterDocumentState): LetterDocumentState =
        applyFirmendaten(
            base.copy(
                headerMode = LETTER_HEADER_LOGO,
                footerMode = LETTER_FOOTER_BUSINESS,
                logoSrc = base.logoSrc.ifBlank { defaultLogoSrc }
            )
        )

    fun applyEmpfaenger(base: LetterDocumentState, key: String): LetterDocumentState {
        if (key == LETTER_EMPFAENGER_FREITEXT) return base.copy(selectedEmpfaengerKey = key)
        val kunde = kunden.firstOrNull { kundeEmpfaengerKey(it) == key }
        return base.copy(
            selectedEmpfaengerKey = key,
            empfaenger = buildKundeEmpfaenger(kunde)
        )
    }

    fun createBlankDocument(layoutKey: String): LetterDocumentState {
        return LetterDocumentState(
            layoutKey = layoutKey,
            selectedUserId = LoginService.getId(),
            signaturName = userSignaturName(),
            headerMode = LETTER_HEADER_LOGO,
            footerMode = LETTER_FOOTER_BUSINESS,
            logoSrc = defaultLogoSrc,
            firmenName = "",
            firmenAdresse = "",
            kopfzeilen = "",
            empfaenger = "",
            betreff = "",
            text = "",
            footerLines = List(LETTER_FOOTER_COUNT) { "" }
        )
    }

    fun buildStateForLayout(layoutKey: String, empfaengerKey: String = LETTER_EMPFAENGER_FREITEXT): LetterDocumentState {
        val base = normalizeDocument(createBlankDocument(layoutKey)).copy(selectedEmpfaengerKey = empfaengerKey)
        return applyEmpfaenger(applyBriefSysDefaults(base), empfaengerKey)
    }

    fun neuesDokument(layoutKey: String = state.layoutKey) {
        if (layoutKey == LETTER_LAYOUT_VOLLMACHT_SYS) {
            bevollmaechtigteName = ""
            bevollmaechtigteSignatur = ""
        }
        state = buildStateForLayout(layoutKey, LETTER_EMPFAENGER_FREITEXT)
        if (layoutKey == LETTER_LAYOUT_VOLLMACHT_SYS) state = state.copy(betreff = "Vollmacht")
    }

    fun changeLayout(layoutKey: String) {
        val normalized = normalizeDocument(state.copy(layoutKey = layoutKey))
        val withLayoutDefaults = if (layoutKey == LETTER_LAYOUT_BRIEF_SYS || layoutKey == LETTER_LAYOUT_VOLLMACHT_SYS || layoutKey.startsWith("PALSCHEIN") || layoutKey.startsWith("DOKUMENTATION") || layoutKey.startsWith("QUALITAET") || layoutKey == LETTER_LAYOUT_PALETTENKONTOABSCHLUSS) {
            applyBriefSysDefaults(normalized)
        } else {
            normalized
        }
        state = applyEmpfaenger(withLayoutDefaults, withLayoutDefaults.selectedEmpfaengerKey)
    }

    suspend fun loadKundenFromNetzwerk(): List<All> {
        val lizByFirmenId = CustomerApi.loadAllDetails()
            .associateBy { it.firmenId.ifBlank { it.id } }

        return RegistrierungApi.loadCompanies()
            .filter { it.istkunde }
            .sortedBy { it.firma.lowercase() }
            .map { firma ->
                val firmenId = firma.firmenId.ifBlank { firma.id }
                val settings = lizByFirmenId[firmenId]
                firma.copy(
                    id = firmenId,
                    firmenId = firmenId,
                    lizenzkey = settings?.lizenzkey.orEmpty(),
                    nodePort = settings?.nodePort.orEmpty(),
                    zustand = settings?.zustand ?: firma.zustand
                )
            }
    }

    fun savePdf() {
        isSaving = true
        scope.launch {
            try {
                delay(250)
                val exportRoot = document.getElementById(LETTER_PREVIEW_ROOT_ID) as? HTMLElement
                if (exportRoot != null) {
                    val fileName = ensurePdfName(state.dokumentName)
                    val pdfFile = renderLetterDocumentPdfFile(exportRoot, fileName)
                    val pdfUrl = window.asDynamic().URL.createObjectURL(pdfFile) as String
                    val downloadLink = document.createElement("a") as HTMLAnchorElement
                    downloadLink.href = pdfUrl
                    downloadLink.download = fileName
                    document.body?.appendChild(downloadLink)
                    downloadLink.click()
                    downloadLink.remove()
                    window.asDynamic().URL.revokeObjectURL(pdfUrl)
                } else {
                    AppDialogs.alert("PDF-Export fehlgeschlagen: Exportdokument wurde nicht gefunden.")
                }
            } catch (t: Throwable) {
                console.error("Letter PDF export failed", t)
                AppDialogs.alert("PDF-Export fehlgeschlagen. Details stehen in der Browser-Konsole.")
            } finally {
                isSaving = false
            }
        }
    }

    fun persistDefaultLogo(logoSrc: String) {
        val userId = LoginService.getId()
        if (userId.isBlank()) return
        scope.launch {
            UserSettingsApi.saveUserSettingsFile(userId, LETTER_TEXT_TO_PDF_LOGO_FILE, logoSrc)
        }
    }

    fun importLogo() {
        val input = document.createElement("input") as? HTMLInputElement ?: return
        input.type = "file"
        input.accept = "image/*"
        input.onchange = {
            val file = input.files?.item(0)
            if (file != null) {
                val reader = FileReader()
                reader.onload = {
                    val result = reader.result as? String
                    if (result != null) {
                        defaultLogoSrc = result
                        state = state.copy(logoSrc = result)
                        persistDefaultLogo(result)
                    }
                    null
                }
                reader.readAsDataURL(file)
            }
            null
        }
        input.click()
    }

    LaunchedEffect(Unit) {
        val userId = LoginService.getId()
        if (userId.isNotBlank()) {
            defaultLogoSrc = resolvedTextToPdfLogo(
                UserSettingsApi.loadUserSettingsFile(userId, LETTER_TEXT_TO_PDF_LOGO_FILE)
            )
        }
        kunden = loadKundenFromNetzwerk()
        unternehmen = KarteiApi.loadUnternehmen() ?: All()
        val weListe = YardApi.loadWeListe()
        val waListe = YardApi.loadWaListe()
        val waLkwListe = YardApi.loadWaLkwListe()
        kontrollDefinitionen = YardApi.loadKontrolleDefinitionen()
        val resolvedWe = weListe.firstOrNull { it.zustand == ZustandManager.ABGESCHLOSSEN } ?: weListe.firstOrNull()
        val resolvedWaKopf = waListe.firstOrNull { it.zustand == ZustandManager.ABGESCHLOSSEN } ?: waListe.firstOrNull()
        val resolvedWaLkw = waLkwListe.firstOrNull { it.zustand == ZustandManager.ABGESCHLOSSEN }
            ?: resolvedWaKopf?.let { kopf -> waLkwListe.firstOrNull { it.lieferscheinnummer == kopf.lieferscheinnummer } }
            ?: waLkwListe.firstOrNull()
        val resolvedWa = resolvedWaLkw?.let { lkw -> mergeWaLetterAuftrag(resolvedWaKopf, lkw) } ?: resolvedWaKopf
        weAuftrag = resolvedWe
        waAuftrag = resolvedWa
        weVorgangslog = resolvedWe?.let { YardApi.loadWeVorgangslog(it.lfdNr.takeIf { nr -> nr > 0 } ?: it.id.toIntOrNull() ?: 0) }.orEmpty()
        waVorgangslog = resolvedWa?.lieferscheinnummer?.takeIf { it.isNotBlank() }?.let { YardApi.loadWaVorgangslog(it) }.orEmpty()
        neuesDokument(LETTER_LAYOUT_BRIEF_SYS)
    }

    val layoutOptionen = listOf(
        All(key = LETTER_LAYOUT_BRIEF_SYS, label = "Brief SYS"),
        All(key = LETTER_LAYOUT_VOLLMACHT_SYS, label = "Vollmacht SYS"),
        All(key = LETTER_LAYOUT_RECHNUNG_SYS, label = "Rechnung SYS"),
        All(key = LETTER_LAYOUT_PALSCHEIN_WE, label = "Palettenschein WE"),
        All(key = LETTER_LAYOUT_PALSCHEIN_WA, label = "Palettenschein WA"),
        All(key = LETTER_LAYOUT_DOKUMENTATION_WE, label = "Dokumentation WE"),
        All(key = LETTER_LAYOUT_DOKUMENTATION_WA, label = "Dokumentation WA"),
        All(key = LETTER_LAYOUT_QUALITAET_WE, label = "Qualitätsdokumentation WE"),
        All(key = LETTER_LAYOUT_QUALITAET_WA, label = "Qualitätsdokumentation WA"),
        All(key = LETTER_LAYOUT_QUALITAET_WE_BERICHT, label = "Wareneingang Fehlerprotokoll"),
        All(key = LETTER_LAYOUT_PALETTENKONTOABSCHLUSS, label = "Palettenkontoabschluss")
    )
    val empfaengerOptionen = listOf(All(key = LETTER_EMPFAENGER_FREITEXT, label = "Freitext")) +
        kunden.map { kunde -> All(key = kundeEmpfaengerKey(kunde), label = buildKundenLabel(kunde)) }

    val renderLetterDocument: @Composable (String, Boolean, String?) -> Unit = { layoutKey, editable, rootId ->
        val effectiveLogoSrc = resolvedTextToPdfLogo(
            state.logoSrc.ifBlank { defaultLogoSrc }
        )
        when (layoutKey) {
            LETTER_LAYOUT_PALSCHEIN_WE,
            LETTER_LAYOUT_DOKUMENTATION_WE,
            LETTER_LAYOUT_QUALITAET_WE,
            LETTER_LAYOUT_QUALITAET_WE_BERICHT,
            LETTER_LAYOUT_PALETTENKONTOABSCHLUSS,
            LETTER_LAYOUT_RECHNUNG_SYS -> RenderSysTextToPdfLayout(
                rootElementId = rootId,
                layoutKey = layoutKey,
                auftrag = when (layoutKey) {
                    LETTER_LAYOUT_RECHNUNG_SYS -> textToPdfInvoiceSample
                    LETTER_LAYOUT_PALSCHEIN_WE,
                    LETTER_LAYOUT_DOKUMENTATION_WE,
                    LETTER_LAYOUT_QUALITAET_WE -> textToPdfProcessWeSample
                    LETTER_LAYOUT_QUALITAET_WE_BERICHT -> textToPdfProcessWeSample
                    LETTER_LAYOUT_PALETTENKONTOABSCHLUSS -> textToPdfPalletAccountSample
                    else -> weAuftrag
                },
                vorgangslog = if (layoutKey == LETTER_LAYOUT_DOKUMENTATION_WE) textToPdfProcessWeLogSample else weVorgangslog,
                unternehmen = unternehmen,
                logoSrc = effectiveLogoSrc,
                kontrollDefinitionen = if (layoutKey == LETTER_LAYOUT_QUALITAET_WE) {
                    textToPdfQmDefinitionSamples
                } else {
                    kontrollDefinitionen.filter { it.aktivWe }
                },
                webKontrolle = if (layoutKey == LETTER_LAYOUT_QUALITAET_WE_BERICHT) textToPdfErrorProtocolSample else null,
                kunde = kunden.firstOrNull { kundeEmpfaengerKey(it) == state.selectedEmpfaengerKey }
                    ?: All(firma = "Musterkunde GmbH", strasse = "Musterstraße", hausnummer = "1", plz = "41836", ort = "Hückelhoven"),
                currentSysUser = LoginService.getUser() ?: All(),
                editable = editable
            )

            LETTER_LAYOUT_PALSCHEIN_WA,
            LETTER_LAYOUT_DOKUMENTATION_WA,
            LETTER_LAYOUT_QUALITAET_WA -> RenderSysTextToPdfLayout(
                rootElementId = rootId,
                layoutKey = layoutKey,
                auftrag = textToPdfProcessWaSample,
                vorgangslog = if (layoutKey == LETTER_LAYOUT_DOKUMENTATION_WA) textToPdfProcessWaLogSample else waVorgangslog,
                unternehmen = unternehmen,
                logoSrc = effectiveLogoSrc,
                kontrollDefinitionen = if (layoutKey == LETTER_LAYOUT_QUALITAET_WA) {
                    textToPdfQmDefinitionSamples
                } else {
                    kontrollDefinitionen.filter { it.aktivWa }
                },
                editable = editable
            )

            LETTER_LAYOUT_BRIEF_SYS -> org.example.project.engineletter.layouts.LetterBriefSysLayout(
                rootElementId = rootId,
                eigeneDatenZeile = org.example.project.engineletter.layouts.buildLetterBriefSysOwnDataLine(
                    firmenName = state.firmenName,
                    firmenAdresse = state.firmenAdresse
                ),
                empfaengerFirmenname = lineAt(state.empfaenger, 0),
                empfaengerStrasseHausnummer = lineAt(state.empfaenger, 1),
                empfaengerPlzOrt = lineAt(state.empfaenger, 2),
                absenderFirmenName = state.firmenName,
                absenderStrasseHausnummer = lineAt(state.firmenAdresse, 0),
                absenderPlzOrt = lineAt(state.firmenAdresse, 1),
                absenderDatum = state.kopfzeilen,
                betreffText = state.betreff,
                briefText = state.text,
                headerMode = state.headerMode,
                footerMode = state.footerMode,
                grussformelText = state.grussformel,
                ortDatumText = state.signaturOrtDatum,
                logoSrc = effectiveLogoSrc,
                footerLines = state.footerLines.normalizedFooterLines(),
                signaturBildSrc = LoginService.getUser()?.signatur.orEmpty(),
                signaturNameText = state.signaturName,
                editable = editable,
                onEmpfaengerFirmennameChange = { state = state.copy(empfaenger = replaceLine(state.empfaenger, 0, it)) },
                onEmpfaengerStrasseHausnummerChange = { state = state.copy(empfaenger = replaceLine(state.empfaenger, 1, it)) },
                onEmpfaengerPlzOrtChange = { state = state.copy(empfaenger = replaceLine(state.empfaenger, 2, it)) },
                onAbsenderFirmenNameChange = { state = state.copy(firmenName = it) },
                onAbsenderStrasseHausnummerChange = { state = state.copy(firmenAdresse = replaceLine(state.firmenAdresse, 0, it)) },
                onAbsenderPlzOrtChange = { state = state.copy(firmenAdresse = replaceLine(state.firmenAdresse, 1, it)) },
                onAbsenderDatumChange = { state = state.copy(kopfzeilen = it) },
                onBetreffChange = { state = state.copy(betreff = it) },
                onTextChange = { state = state.copy(text = it) },
                onGrussformelChange = { state = state.copy(grussformel = it) },
                onSignaturOrtDatumChange = { state = state.copy(signaturOrtDatum = it) },
                onSignaturNameChange = { state = state.copy(signaturName = it) },
                onFooterLineChange = { index, value ->
                    state = state.copy(footerLines = state.footerLines.updatedFooterLine(index, value))
                }
            )

            LETTER_LAYOUT_VOLLMACHT_SYS -> VollmachtSysLayout(
                rootElementId = rootId,
                eigeneDatenZeile = org.example.project.engineletter.layouts.buildLetterBriefSysOwnDataLine(
                    firmenName = state.firmenName,
                    firmenAdresse = state.firmenAdresse
                ),
                empfaengerFirmenname = lineAt(state.empfaenger, 0),
                empfaengerStrasseHausnummer = lineAt(state.empfaenger, 1),
                empfaengerPlzOrt = lineAt(state.empfaenger, 2),
                absenderFirmenName = state.firmenName,
                absenderStrasseHausnummer = lineAt(state.firmenAdresse, 0),
                absenderPlzOrt = lineAt(state.firmenAdresse, 1),
                absenderDatum = state.kopfzeilen,
                betreffText = state.betreff,
                briefText = state.text,
                logoSrc = effectiveLogoSrc,
                headerMode = state.headerMode,
                footerMode = state.footerMode,
                footerLines = state.footerLines.normalizedFooterLines(),
                grussformelText = state.grussformel,
                ortDatumText = state.signaturOrtDatum,
                vollmachtgeberSignatur = LoginService.getUser()?.signatur.orEmpty(),
                vollmachtgeberName = state.signaturName,
                bevollmaechtigteName = bevollmaechtigteName,
                bevollmaechtigteSignatur = bevollmaechtigteSignatur,
                editable = editable,
                onEmpfaengerFirmennameChange = { state = state.copy(empfaenger = replaceLine(state.empfaenger, 0, it)) },
                onEmpfaengerStrasseHausnummerChange = { state = state.copy(empfaenger = replaceLine(state.empfaenger, 1, it)) },
                onEmpfaengerPlzOrtChange = { state = state.copy(empfaenger = replaceLine(state.empfaenger, 2, it)) },
                onAbsenderFirmenNameChange = { state = state.copy(firmenName = it) },
                onAbsenderStrasseHausnummerChange = { state = state.copy(firmenAdresse = replaceLine(state.firmenAdresse, 0, it)) },
                onAbsenderPlzOrtChange = { state = state.copy(firmenAdresse = replaceLine(state.firmenAdresse, 1, it)) },
                onAbsenderDatumChange = { state = state.copy(kopfzeilen = it) },
                onBetreffChange = { state = state.copy(betreff = it) },
                onTextChange = { state = state.copy(text = it) },
                onGrussformelChange = { state = state.copy(grussformel = it) },
                onSignaturOrtDatumChange = { state = state.copy(signaturOrtDatum = it) },
                onSignaturNameChange = { state = state.copy(signaturName = it) },
                onFooterLineChange = { index, value -> state = state.copy(footerLines = state.footerLines.updatedFooterLine(index, value)) },
                onBevollmaechtigteNameChange = { bevollmaechtigteName = it }
            )

            else -> org.example.project.engineletter.layouts.LetterBriefSysLayout(
                rootElementId = rootId,
                eigeneDatenZeile = org.example.project.engineletter.layouts.buildLetterBriefSysOwnDataLine(
                    firmenName = state.firmenName,
                    firmenAdresse = state.firmenAdresse
                ),
                empfaengerFirmenname = lineAt(state.empfaenger, 0),
                empfaengerStrasseHausnummer = lineAt(state.empfaenger, 1),
                empfaengerPlzOrt = lineAt(state.empfaenger, 2),
                absenderFirmenName = state.firmenName,
                absenderStrasseHausnummer = lineAt(state.firmenAdresse, 0),
                absenderPlzOrt = lineAt(state.firmenAdresse, 1),
                absenderDatum = state.kopfzeilen,
                betreffText = state.betreff,
                briefText = state.text,
                headerMode = state.headerMode,
                footerMode = state.footerMode,
                grussformelText = state.grussformel,
                ortDatumText = state.signaturOrtDatum,
                logoSrc = effectiveLogoSrc,
                footerLines = state.footerLines.normalizedFooterLines(),
                signaturBildSrc = LoginService.getUser()?.signatur.orEmpty(),
                signaturNameText = state.signaturName,
                editable = editable,
                onEmpfaengerFirmennameChange = { state = state.copy(empfaenger = replaceLine(state.empfaenger, 0, it)) },
                onEmpfaengerStrasseHausnummerChange = { state = state.copy(empfaenger = replaceLine(state.empfaenger, 1, it)) },
                onEmpfaengerPlzOrtChange = { state = state.copy(empfaenger = replaceLine(state.empfaenger, 2, it)) },
                onAbsenderFirmenNameChange = { state = state.copy(firmenName = it) },
                onAbsenderStrasseHausnummerChange = { state = state.copy(firmenAdresse = replaceLine(state.firmenAdresse, 0, it)) },
                onAbsenderPlzOrtChange = { state = state.copy(firmenAdresse = replaceLine(state.firmenAdresse, 1, it)) },
                onAbsenderDatumChange = { state = state.copy(kopfzeilen = it) },
                onBetreffChange = { state = state.copy(betreff = it) },
                onTextChange = { state = state.copy(text = it) },
                onGrussformelChange = { state = state.copy(grussformel = it) },
                onSignaturOrtDatumChange = { state = state.copy(signaturOrtDatum = it) },
                onSignaturNameChange = { state = state.copy(signaturName = it) },
                onFooterLineChange = { index, value ->
                    state = state.copy(footerLines = state.footerLines.updatedFooterLine(index, value))
                }
            )
        }
    }

    LetterEngineLayout(
        title = "Text to PDF",
        showTitle = false,
        scaleToPage = !showLayoutGallery,
        buttonBar = {
            LetterButtonBar(
                layoutOptionen = layoutOptionen,
                selectedLayoutKey = state.layoutKey,
                empfaengerOptionen = empfaengerOptionen,
                selectedEmpfaengerKey = state.selectedEmpfaengerKey,
                isSaving = isSaving,
                documentActive = !showLayoutGallery,
                onNeu = { showLayoutGallery = true },
                onLayoutChange = { key -> changeLayout(key); showLayoutGallery = false },
                onEmpfaengerChange = { key -> state = applyEmpfaenger(state, key) },
                onLogoImport = { importLogo() },
                onSave = { if (!showLayoutGallery) savePdf() },
                onUnterzeichnen = if (!showLayoutGallery && state.layoutKey == LETTER_LAYOUT_VOLLMACHT_SYS) {
                    { vollmachtCanvasGezeichnet = false; vollmachtSignaturOffen = true }
                } else null
            )
        }
    ) {
        if (showLayoutGallery) {
            LetterLayoutGallery(
                layouts = layoutOptionen,
                preview = { key -> renderLetterDocument(key, false, null) },
                onSelect = { key -> neuesDokument(key); showLayoutGallery = false }
            )
        } else {
            renderLetterDocument(state.layoutKey, true, LETTER_PREVIEW_ROOT_ID)
        }
    }

    if (vollmachtSignaturOffen) {
        SignaturDatenschutzOverlay(
            canvasId = LETTER_VOLLMACHT_SIGNATURE_CANVAS_ID,
            datenschutzText = "",
            datenschutzLabel = "",
            buttonLabel = "Unterschrift übernehmen",
            signaturHeaderText = "Bitte unterschreiben Sie im Feld.",
            canvasHoehePx = 200,
            maxWidthPx = 780,
            overlayStyle = {},
            cardStyle = {},
            canvasGezeichnet = vollmachtCanvasGezeichnet,
            onCanvasGezeichnetChange = { vollmachtCanvasGezeichnet = it },
            datenschutzAkzeptiert = true,
            onDatenschutzAkzeptiertChange = {},
            fehlerText = null,
            onComplete = {
                bevollmaechtigteSignatur = it
                vollmachtSignaturOffen = false
            }
        )
    }
}

@Composable
private fun LetterLayoutGallery(
    layouts: List<All>,
    preview: @Composable (String) -> Unit,
    onSelect: (String) -> Unit
) {
    Div({
        style {
            width(100.percent)
            maxWidth(1120.px)
            property("margin", "0 auto")
            padding(18.px, 12.px, 48.px, 12.px)
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fill, 280px)")
            justifyContent(JustifyContent.Center)
            gap(30.px)
            property("box-sizing", "border-box")
        }
    }) {
        layouts.forEach { layout ->
            Div({
                attr("role", "button")
                attr("tabindex", "0")
                onClick { onSelect(layout.key) }
                style {
                    cursor("pointer")
                    width(280.px)
                    border(2.px, LineStyle.Solid, Color("#6f829a"))
                    borderRadius(10.px)
                    backgroundColor(Color("#182331"))
                    property("overflow", "hidden")
                    property("box-shadow", "0 8px 24px rgba(0,0,0,.30)")
                    property("box-sizing", "border-box")
                }
            }) {
                Div({
                    style {
                        position(Position.Relative)
                        width(100.percent)
                        property("aspect-ratio", "210 / 297")
                        backgroundColor(Color.white)
                        borderRadius(0.px)
                        property("box-sizing", "border-box")
                        property("overflow", "hidden")
                    }
                }) {
                    Div({
                        style {
                            position(Position.Absolute)
                            top(0.px)
                            left(0.px)
                            width(794.px)
                            property("transform", "scale(0.35)")
                            property("transform-origin", "top left")
                            property("pointer-events", "none")
                        }
                    }) { preview(layout.key) }
                }
                Div({ style { padding(12.px, 8.px); color(Color.white); fontSize(15.px); fontWeight("600"); textAlign("center"); property("border-top", "1px solid #6f829a") } }) { Text(layout.label) }
            }
        }
    }
}

private fun mergeWaLetterAuftrag(kopf: All?, lkw: All): All =
    lkw.copy(
        datum = lkw.datum.ifBlank { kopf?.datum.orEmpty() },
        lieferant = lkw.lieferant.ifBlank { kopf?.lieferant.orEmpty() },
        empfaenger = lkw.empfaenger.ifBlank { kopf?.empfaenger.orEmpty() },
        auftraggeber = lkw.auftraggeber.ifBlank { kopf?.auftraggeber.orEmpty() },
        unternehmer = lkw.unternehmer.ifBlank { kopf?.unternehmer.orEmpty() },
        ort = lkw.ort.ifBlank { kopf?.ort.orEmpty() },
        strasse = lkw.strasse.ifBlank { kopf?.strasse.orEmpty() },
        hausnummer = lkw.hausnummer.ifBlank { kopf?.hausnummer.orEmpty() },
        plz = lkw.plz.ifBlank { kopf?.plz.orEmpty() },
        firma = lkw.firma.ifBlank { kopf?.firma.orEmpty() },
        firmenId = lkw.firmenId.ifBlank { kopf?.firmenId.orEmpty() },
        palettenWerte = lkw.palettenWerte.ifEmpty { kopf?.palettenWerte.orEmpty() },
        slotStandortId = lkw.slotStandortId.ifBlank { kopf?.slotStandortId.orEmpty() },
        waStandortId = lkw.waStandortId.ifBlank { kopf?.waStandortId.orEmpty() },
        waSammelId = lkw.waSammelId.ifBlank { kopf?.waSammelId.orEmpty() },
        waSammelPin = lkw.waSammelPin.ifBlank { kopf?.waSammelPin.orEmpty() }
    )

private fun buildKundenLabel(kunde: All): String {
    val name = kunde.firma
        .ifBlank { kunde.ansprechpartner }
        .ifBlank { kunde.firmenId }
    val nummer = kunde.firmenId
    return if (nummer.isBlank() || nummer == name) name else "$name ($nummer)"
}

private fun kundeEmpfaengerKey(kunde: All): String = kunde.firmenId

private fun buildAddressBlock(
    strasse: String,
    hausnummer: String,
    plz: String,
    ort: String
): String {
    val zeile1 = listOf(strasse, hausnummer).filter { it.isNotBlank() }.joinToString(" ")
    val zeile2 = listOf(plz, ort).filter { it.isNotBlank() }.joinToString(" ")
    val zeilen = listOf(zeile1, zeile2).filter { it.isNotBlank() }
    return zeilen.joinToString("\n")
}

private fun buildKundeEmpfaenger(kunde: All?): String {
    if (kunde == null) return ""
    val kopf = kunde.firma.ifBlank { kunde.ansprechpartner }
    val adresse = buildAddressBlock(
        strasse = kunde.strasse,
        hausnummer = kunde.hausnummer,
        plz = kunde.plz,
        ort = kunde.ort
    )
    return listOfNotNull(kopf.takeIf { it.isNotBlank() }, adresse.takeIf { it.isNotBlank() }).joinToString("\n")
}

private fun ensurePdfName(value: String): String {
    val base = value.ifBlank { "dokument.pdf" }
    return if (base.lowercase().endsWith(".pdf")) base else "$base.pdf"
}

private fun lineAt(value: String, index: Int): String =
    value.replace("\r\n", "\n")
        .split("\n")
        .getOrNull(index)
        .orEmpty()

private fun replaceLine(value: String, index: Int, replacement: String): String {
    val lines = value.replace("\r\n", "\n").split("\n").toMutableList()
    while (lines.size <= index) lines += ""
    lines[index] = replacement
    return lines.joinToString("\n").trimEnd()
}

private fun List<String>.normalizedFooterLines(): List<String> =
    List(LETTER_FOOTER_COUNT) { index -> this.getOrNull(index).orEmpty() }

private fun List<String>.updatedFooterLine(index: Int, value: String): List<String> =
    normalizedFooterLines().toMutableList().apply { this[index] = value }.toList()

private fun List<String>.withFallback(fallback: List<String>): List<String> =
    normalizedFooterLines().mapIndexed { index, value -> value.ifBlank { fallback.getOrNull(index).orEmpty() } }

private fun buildUnternehmenFooterLines(
    firmenName: String,
    firmenAdresse: String,
    webseite: String,
    email: String,
    telefon: String,
    fax: String,
    kreditinstitut: String,
    iban: String,
    umsatzsteuerId: String
): List<String> =
    listOf(
        org.example.project.engineletter.layouts.buildLetterBriefSysOwnDataLine(
            firmenName = firmenName,
            firmenAdresse = firmenAdresse
        ),
        listOf(webseite, email, telefon, fax).filter { it.isNotBlank() }.joinToString(" | "),
        listOf(kreditinstitut, iban, umsatzsteuerId).filter { it.isNotBlank() }.joinToString(" | ")
    ).normalizedFooterLines()





